import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounceCallback } from "@react-hook/debounce";
import { isEqual } from "lodash";
import type { FilterOptionOption } from "react-select/dist/declarations/src/filters";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import usePrevious from "utils/usePrevious";
import useLatestRef from "utils/useLatestRef";
import MenuListWithResultCount, {
  PlaceholderOption,
  isPlaceholderOption,
} from "./MenuListWithResultCount";
import { isNewOption, NewOption } from "./utils";

function filterOption<Option extends object>(
  option: FilterOptionOption<Option | NewOption | PlaceholderOption>,
) {
  if (isNewOption(option.data)) return true;
  // Filter out placeholder objects
  return !isPlaceholderOption(option.data);
}

type LoadOptionsCallback<Option> = (options: readonly Option[]) => void;

type FetchResult<Option> = {
  options: Option[] | readonly Option[];
  totalCount?: number;
};

type UseAsyncFetchOptionsOptions<Option> = {
  cacheUsing?: unknown;
  debounceDuration?: number;
  fetchOptions: (
    inputValue: string,
  ) => Promise<Option[] | readonly Option[] | FetchResult<Option>>;
  /** The maximum duration in seconds to cache the options for. */
  maxCacheAge?: number;
  minInputLength?: number;
};

/**
 * A hook to be used with the `AsyncSelect` `react-select` component that
 * primarily simplifies the handling of debounced option fetching.
 */
export default function useAsyncFetchOptions<Option>({
  cacheUsing,
  debounceDuration = 500,
  fetchOptions: performFetch,
  maxCacheAge = 30,
  minInputLength = 2,
}: UseAsyncFetchOptionsOptions<Option>) {
  const [cacheKey, setCacheKey] = useState<true | number>(true);

  const { showErrorToast } = useAsyncCompleteToast();

  const clearCacheTimeoutRef = useRef<NodeJS.Timeout>();
  const prevCacheUsing = usePrevious(cacheUsing);
  const latestMaxCacheAgeRef = useLatestRef(maxCacheAge);

  useEffect(() => {
    if (!clearCacheTimeoutRef.current) {
      clearCacheTimeoutRef.current = setInterval(() => {
        setCacheKey((prev) => (typeof prev === "number" ? prev + 1 : 1));
      }, latestMaxCacheAgeRef.current * 1000);
    }

    if (
      (cacheUsing !== undefined || prevCacheUsing !== undefined) &&
      !isEqual(cacheUsing, prevCacheUsing)
    ) {
      setCacheKey((prev) => (typeof prev === "number" ? prev + 1 : 1));
    }

    return () => {
      if (clearCacheTimeoutRef.current) {
        clearInterval(clearCacheTimeoutRef.current);
        clearCacheTimeoutRef.current = undefined;
      }
    };
  }, [cacheUsing, prevCacheUsing, latestMaxCacheAgeRef]);

  const fetchOptions = useCallback(
    (inputValue: string, callback: LoadOptionsCallback<Option>) => {
      if (!inputValue || inputValue.length < minInputLength) {
        return callback([]);
      }

      performFetch(inputValue)
        .then((result) => {
          // Check if result has the FetchResult shape with totalCount
          if (result && typeof result === "object" && "options" in result) {
            const { options, totalCount } = result;

            if (totalCount && totalCount > options.length) {
              // Create array with actual options plus placeholder objects
              const placeholderCount = totalCount - options.length;
              const placeholders: PlaceholderOption[] = Array.from(
                { length: placeholderCount },
                // eslint-disable-next-line @typescript-eslint/naming-convention
                (): PlaceholderOption => ({ __isPlaceholder__: true }),
              );

              // Cast is safe because filterOption will filter out placeholders
              callback([...options, ...placeholders] as Option[]);
            } else {
              // No need for placeholders if all results are returned
              callback(options);
            }
          } else {
            // Legacy: just an array of options
            callback(result);
          }
        })
        .catch((reason) => {
          showErrorToast(reason, {
            title: "An error occurred loading options",
          });
          callback([]);
        });
    },
    [performFetch, showErrorToast, minInputLength],
  );

  const debouncedFetchOptions = useDebounceCallback(
    fetchOptions,
    debounceDuration,
    false,
  );

  const loadOptions = useCallback(
    (inputValue: string, callback: LoadOptionsCallback<Option>) => {
      debouncedFetchOptions(inputValue, callback);
    },
    [debouncedFetchOptions],
  );

  return {
    cacheOptions: cacheKey,
    components: { MenuList: MenuListWithResultCount },
    filterOption,
    loadOptions,
  };
}
